import { useState, type PropsWithChildren } from 'react';
import { CircleUserRound, Heart, Menu, X } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { legalLinks } from '../lib/legal';
import { halsArticle, motorboatExamArticle } from '../../shared/articles';

interface Props extends PropsWithChildren { session: Session | null; preview: boolean }

export function AppShell({ children, session, preview }: Props) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const learning = pathname === '/' || /^\/(sm|zj|nauka|egzamin)(\/|$)/.test(pathname);
  const readingGuide = [halsArticle, motorboatExamArticle].some(article => article.path.replace(/\/$/, '') === pathname.replace(/\/$/, ''));
  return <div className="app-shell">
    <a className="skip-link" href="#main">Przejdź do treści</a>
    {preview && <div className="preview-bar" role="status">Środowisko testowe — wersja przed publikacją.</div>}
    <header className="site-header">
      <Link to="/" className="brand" aria-label="Przed rejsem — strona główna">
        <span className="brand-mark"><svg viewBox="0 0 56 44" fill="none" aria-hidden="true"><path d="M30 3 17 31h12L30 3Z" fill="currentColor" /><path d="m33 9 8 23h-9l1-23Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M3 39c16-7 31 5 50-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg></span>
        <span>przed rejsem</span>
      </Link>
      <button className="menu-button" onClick={() => setOpen(!open)} aria-label={open ? 'Zamknij menu' : 'Otwórz menu'} aria-expanded={open} aria-controls="main-navigation">
        {open ? <X /> : <Menu />}
      </button>
      <nav id="main-navigation" className={open ? 'main-nav open' : 'main-nav'} aria-label="Główna nawigacja" onClick={() => setOpen(false)} onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}>
        <Link className={learning ? 'active' : undefined} to="/#wybierz">Nauka</Link>
        <NavLink to="/dashboard">Postępy</NavLink>
        <NavLink to="/poradniki" className={readingGuide ? 'active' : undefined}>Poradniki</NavLink>
        <NavLink to="/napisz-artykul">Napisz artykuł</NavLink>
        <NavLink to="/dodaj-pytanie">Dodaj pytanie</NavLink>
        <a className="support-link" href="https://buycoffee.to/przedrejsem" target="_blank" rel="noreferrer"><Heart size={16} aria-hidden="true" /> Wesprzyj</a>
        <NavLink className="account-link" to={session ? '/konto' : '/logowanie'}><CircleUserRound size={18} aria-hidden="true" /> {session ? 'Konto' : 'Zaloguj'}</NavLink>
      </nav>
    </header>
    <main id="main">{children}</main>
    <aside className="storage-notice" aria-label="Informacja o cookies">
      <p>Serwis używa cookies i pamięci przeglądarki do logowania i funkcji nauki. <Link to="/cookies">Dowiedz się więcej</Link>.</p>
    </aside>
    <footer className="site-footer">
      <div><strong>Przed rejsem</strong><p>Autorska pomoc w nauce teorii. Nie jest oficjalną bazą komisji ani gwarancją zdania.</p></div>
      <nav aria-label="Stopka">
        <Link to="/poradniki/">Poradniki</Link>
        <Link to="/sm/">Testy na sternika motorowodnego</Link><Link to="/zj/">Testy na żeglarza jachtowego</Link><Link to="/egzamin/sm/">Próbny egzamin SM</Link><Link to="/egzamin/zj/">Próbny egzamin ŻJ</Link><Link to="/o-projekcie">O projekcie</Link><Link to="/napisz-artykul">Napisz artykuł</Link><Link to="/kontakt">Kontakt</Link>{legalLinks.map(([route, label]) => <Link key={route} to={route}>{label}</Link>)}
      </nav>
      <small>© {new Date().getFullYear()} Przed rejsem</small>
    </footer>
  </div>;
}
